import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'sonner';

const AzureAIChatContext = createContext(null);

export const useAzureAIChat = () => {
  const context = useContext(AzureAIChatContext);
  if (!context) {
    throw new Error('useAzureAIChat must be used within AzureAIChatProvider');
  }
  return context;
};

export const AzureAIChatProvider = ({ children }) => {
  // Widget state
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);

  // Backend URL from environment variable
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

  // Widget actions
  const openWidget = useCallback(() => {
    setIsOpen(true);
    setUnreadCount(0);
  }, []);

  const closeWidget = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Chat actions
  const sendMessage = useCallback(async (text) => {
    if (!text.trim()) return;

    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsAssistantTyping(true);

    console.log('[DigianChat] Sending message:', text);
    console.log('[DigianChat] Backend URL:', backendUrl);

    try {
      const requestBody = {
        message: text,
        conversation_history: messages
      };

      console.log('[DigianChat] Request body:', requestBody);

      const response = await fetch(`${backendUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('[DigianChat] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('[DigianChat] Error response:', errorData);
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[DigianChat] Response data:', data);

      if (!data.response) {
        console.error('[DigianChat] No response in data:', data);
        throw new Error('No response from server');
      }

      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Increment unread count if widget is closed
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }

    } catch (error) {
      console.error('[DigianChat] Failed to send message:', error);
      toast.error(`Failed to send message: ${error.message}`);

      // Add error message to chat
      const errorMessage = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}. Please try again or contact support if the problem persists.`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsAssistantTyping(false);
    }
  }, [messages, backendUrl, isOpen]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const value = {
    // Widget state
    isOpen,
    unreadCount,

    // Chat state
    messages,
    isAssistantTyping,

    // Actions
    openWidget,
    closeWidget,
    sendMessage,
    clearMessages
  };

  return (
    <AzureAIChatContext.Provider value={value}>
      {children}
    </AzureAIChatContext.Provider>
  );
};
